const DAY_MS = 24 * 60 * 60 * 1000;
const ATTENTION_PCT = 20;
const RATE_LBS_PER_DAY = {
  low: 5,
  avg: 10,
  high: 20,
  very_high: 30,
};

function isPoint(location) {
  return location?.geojson?.type === 'Point';
}

function featureTypeOf(location) {
  return String(location?.feature_type || '').toLowerCase();
}

/** Sit / tag-out pin. Legacy rows with a blank feature_type stay stands. */
export function isClubStandLocation(location) {
  if (!isPoint(location)) return false;
  const type = featureTypeOf(location);
  if (type === 'other' || type === 'feeder') return false;
  return type === 'stand' || type === '' || location?.is_stand === true;
}

/** Feeder switch on (or legacy feature_type feeder). Independent of stand. */
export function isClubFeedLocation(location) {
  if (!isPoint(location)) return false;
  return Boolean(location?.feeding_mode) || featureTypeOf(location) === 'feeder';
}

/** Neither switch — named marker only. */
export function isClubMarkerLocation(location) {
  if (!isPoint(location)) return false;
  return !isClubStandLocation(location) && !isClubFeedLocation(location);
}

function byName(a, b) {
  return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
    sensitivity: 'base',
  });
}

export function partitionClubWaypoints(huntLocations) {
  const stands = [];
  const feeders = [];
  const markers = [];
  (Array.isArray(huntLocations) ? huntLocations : []).forEach((location) => {
    if (isClubStandLocation(location)) stands.push(location);
    if (isClubFeedLocation(location)) feeders.push(location);
    if (isClubMarkerLocation(location)) markers.push(location);
  });
  stands.sort(byName);
  feeders.sort(byName);
  markers.sort(byName);
  return { stands, feeders, markers };
}

export function sameWaypointId(a, b) {
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
}

export function clubFeederDaysLeft(station, now = new Date()) {
  if (station?.feeding_is_active === false) return null;
  const remaining = Number(station?.estimated_remaining_lbs);
  const rate =
    Number(station?.consumption_rate_lbs_per_day) ||
    RATE_LBS_PER_DAY[String(station?.consumption_rate_code || '').toLowerCase()] ||
    0;
  if (Number.isFinite(remaining) && rate > 0) {
    return Math.max(0, Math.ceil(remaining / rate));
  }
  if (!station?.refill_due_date) return null;
  const due = new Date(station.refill_due_date);
  if (Number.isNaN(due.getTime())) return null;
  return Math.max(0, Math.ceil((due.getTime() - now.getTime()) / DAY_MS));
}

export function clubFeederDaysLabel(station, now = new Date()) {
  if (station?.feeding_is_active === false) return 'OFF';
  const pct = Number(station?.percent_remaining);
  if (Number.isFinite(pct) && pct <= 0) return 'EMPTY';
  const days = clubFeederDaysLeft(station, now);
  if (days == null) return '—';
  return `${days} DAYS`;
}

/** Personal board status line: lb/week from the same rate table as days-left. */
export function clubFeederLbsPerWeek(station) {
  const rate =
    Number(station?.consumption_rate_lbs_per_day) ||
    RATE_LBS_PER_DAY[String(station?.consumption_rate_code || '').toLowerCase()] ||
    0;
  if (!(rate > 0)) return null;
  return rate * 7;
}

export function clubFeederModeLabel(station) {
  const mode = String(station?.feeding_mode || '').toLowerCase();
  if (mode === 'hopper') return 'Hopper';
  if (mode === 'ground') return 'On the ground';
  return '';
}

export function clubFeederLbsPair(station) {
  const remaining = station?.estimated_remaining_lbs;
  const capacity = station?.feed_capacity_lbs;
  if (remaining == null && capacity == null) return '';
  if (capacity != null && Number(capacity) > 0) {
    const left = remaining == null ? '—' : String(Math.round(Number(remaining)));
    return `${left} / ${Math.round(Number(capacity))} lb`;
  }
  if (remaining == null) return '';
  return `${Math.round(Number(remaining))} lb left`;
}

export function clubFeederMeta(station) {
  const feed = String(station?.default_feed_type || '').trim();
  const feedLabel = feed
    ? feed.replace(/\b\w/g, (ch) => ch.toUpperCase())
    : '';
  return [clubFeederModeLabel(station), feedLabel, clubFeederLbsPair(station)]
    .filter(Boolean)
    .join(' · ');
}

export function clubFeederFillPercent(station) {
  const pct = Number(station?.percent_remaining);
  if (Number.isFinite(pct)) return Math.max(0, Math.min(100, pct));
  const remaining = Number(station?.estimated_remaining_lbs);
  const capacity = Number(station?.feed_capacity_lbs);
  if (Number.isFinite(remaining) && Number.isFinite(capacity) && capacity > 0) {
    return Math.max(0, Math.min(100, (remaining / capacity) * 100));
  }
  return remaining > 0 ? 100 : 0;
}

export function clubFeederBarTone(station) {
  if (station?.feeding_is_active === false) return 'off';
  const pct = clubFeederFillPercent(station);
  if (pct <= 0) return 'low';
  if (pct < ATTENTION_PCT) return 'low';
  if (pct < 40) return 'watch';
  return 'ok';
}

export function clubFeederNeedsFill(station) {
  if (station?.feeding_is_active === false) return false;
  const tone = clubFeederBarTone(station);
  return tone === 'low';
}

export function clubFillAgo(iso, now = new Date()) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const days = Math.max(0, Math.floor((now.getTime() - then.getTime()) / DAY_MS));
  if (days === 0) return 'TODAY';
  return `${days}D AGO`;
}

export function clubFeederLastFillLine(station, now = new Date()) {
  const ago = clubFillAgo(station?.last_fill_at, now);
  if (!ago) return 'NO FILLS YET';
  const name = String(station?.last_fill_user_name || '').trim().toUpperCase() || 'MEMBER';
  return `LAST FILL ${name} · ${ago}`;
}

export function clubFeedNeedsFillCount(feeders) {
  return (Array.isArray(feeders) ? feeders : []).filter(clubFeederNeedsFill).length;
}

export function clubFeedTotalCopy({
  stationCount = 0,
  seasonLbs = 0,
  memberCount = null,
} = {}) {
  const stations = Number(stationCount) || 0;
  const lbs = Math.round(Math.max(0, Number(seasonLbs) || 0)).toLocaleString('en-US');
  const stationBit = stations === 1 ? '1 station' : `${stations} stations`;
  const members = Number(memberCount);
  const memberBit =
    Number.isFinite(members) && members > 0
      ? ` · ${members} ${members === 1 ? 'member' : 'members'}`
      : '';
  return {
    lbsLabel: lbs,
    detail: `Across ${stationBit}${memberBit}`,
  };
}
